import React, { useEffect, useRef, useState } from 'react';
import {
  BORDER,
  BORDER_STRONG,
  BottomSheet,
  CHIP_TEAL_BG,
  DESTRUCTIVE,
  Icon,
  MONO,
  Pill,
  SelectBox,
  TEAL,
  TEAL_DARK,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  t,
} from '../../components/list';
import { useInfiniteScroll } from '../../hook/useInfiniteScroll';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import PhotoIcon from '../../images/PhotoIcon';

/**
 * Mobile (< 1024px) building blocks for the Attribute create / edit screen.
 * Purely presentational: every piece takes its data and callbacks from
 * CreateAttributes.tsx, which keeps owning the state and the API calls.
 */

/* ------------------------------------------------------------------ */
/* Tokens                                                              */
/* ------------------------------------------------------------------ */

export const MOBILE_HEADER_HEIGHT = 56;
const PROGRESS_HEIGHT = 4;
const STICKY_TOP = MOBILE_HEADER_HEIGHT + PROGRESS_HEIGHT;

/** 48px input, 16px text (no iOS auto-zoom), 12px radius. */
export const mobileInputStyle: React.CSSProperties = {
  width: '100%',
  height: '48px',
  padding: '0 14px',
  fontSize: '16px',
  lineHeight: '24px',
  color: TEXT,
  border: `1px solid ${BORDER}`,
  borderRadius: '12px',
  backgroundColor: '#FFFFFF',
  outline: 'none',
};

const errorInputStyle: React.CSSProperties = { borderColor: DESTRUCTIVE };

const INPUT_FOCUS = 'focus:border-[#0F7679]';

/* ------------------------------------------------------------------ */
/* Header + progress                                                   */
/* ------------------------------------------------------------------ */

export const MobileHeader: React.FC<{
  title: string;
  step: number;
  steps: number;
  onBack: () => void;
  backLabel: string;
}> = ({ title, step, steps, onBack, backLabel }) => (
  <div style={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: '#FFFFFF' }}>
    <div
      className="flex items-center"
      style={{ height: `${MOBILE_HEADER_HEIGHT}px`, padding: '0 4px 0 4px' }}
    >
      <button
        type="button"
        aria-label={backLabel}
        onClick={onBack}
        className="flex items-center justify-center cursor-pointer"
        style={{
          width: '44px',
          height: '44px',
          border: 'none',
          background: 'transparent',
          padding: 0,
          flex: 'none',
        }}
      >
        <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
          <Icon name="chev" color={TEXT} size={22} />
        </span>
      </button>
      <p
        className="font-inter"
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'center',
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '24px',
          color: TEXT,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </p>
      <p
        className="font-inter"
        style={{
          width: '44px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 500,
          color: TEXT_DIM,
          flex: 'none',
        }}
      >
        {step}/{steps}
      </p>
    </div>
    <div className="flex" style={{ height: `${PROGRESS_HEIGHT}px`, gap: '4px', padding: '0 16px' }}>
      {Array.from({ length: steps }, (_, i) => (
        <div
          key={`progress-${i}`}
          style={{
            flex: 1,
            borderRadius: '999px',
            backgroundColor: i < step ? TEAL : BORDER,
          }}
        />
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Fields                                                              */
/* ------------------------------------------------------------------ */

export const MobileField = React.forwardRef<
  HTMLDivElement,
  { label: string; required?: boolean; error?: string; children: React.ReactNode; hint?: string }
>(({ label, required, error, children, hint }, ref) => (
  <div ref={ref}>
    <p
      className="font-inter"
      style={{
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        color: TEXT_MUTED,
        marginBottom: '6px',
      }}
    >
      {label}
      {required && <span style={{ color: DESTRUCTIVE }}> *</span>}
    </p>
    {children}
    {error ? (
      <p
        role="alert"
        className="font-inter"
        style={{ fontSize: '13px', lineHeight: '18px', color: DESTRUCTIVE, marginTop: '6px' }}
      >
        {error}
      </p>
    ) : hint ? (
      <p
        className="font-inter"
        style={{ fontSize: '12.5px', lineHeight: '18px', color: TEXT_DIM, marginTop: '6px' }}
      >
        {hint}
      </p>
    ) : null}
  </div>
));
MobileField.displayName = 'MobileField';

export const MobileTextInput: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
> = ({ invalid, style, className, ...rest }) => (
  <input
    {...rest}
    className={`font-inter ${INPUT_FOCUS} ${className ?? ''}`}
    style={{ ...mobileInputStyle, ...(invalid ? errorInputStyle : null), ...style }}
  />
);

/** 2–4 way choice as a row of 44px pills. */
export const Segmented = <V extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: { value: V; label: string }[];
  value: V;
  onChange: (value: V) => void;
  ariaLabel?: string;
}) => (
  <div role="radiogroup" aria-label={ariaLabel} className="flex" style={{ gap: '8px' }}>
    {options.map(option => {
      const on = option.value === value;
      return (
        <button
          key={String(option.value)}
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => onChange(option.value)}
          className="font-inter cursor-pointer"
          style={{
            flex: 1,
            height: '44px',
            borderRadius: '30px',
            border: `1px solid ${on ? TEAL : BORDER_STRONG}`,
            backgroundColor: on ? TEAL : '#FFFFFF',
            color: on ? '#FFFFFF' : TEXT,
            fontSize: '15px',
            fontWeight: 500,
            padding: '0 12px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

/** A 48px tappable row that opens a picker sheet: value (or placeholder) + chevron. */
export const PickerRow: React.FC<{
  value?: string;
  placeholder: string;
  onClick: () => void;
  leading?: React.ReactNode;
  ariaLabel?: string;
}> = ({ value, placeholder, onClick, leading, ariaLabel }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className="flex items-center cursor-pointer font-inter"
    style={{ ...mobileInputStyle, gap: '10px', textAlign: 'left' }}
  >
    {leading}
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontSize: '16px',
        color: value ? TEXT : TEXT_DIM,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {value || placeholder}
    </span>
    <Icon name="chevd" color={TEXT_DIM} size={20} />
  </button>
);

/* ------------------------------------------------------------------ */
/* Sheet primitives                                                    */
/* ------------------------------------------------------------------ */

/** 48px choice row inside a bottom sheet: label, optional subline, check when selected. */
export const ChoiceRow: React.FC<{
  label: string;
  sub?: string;
  selected?: boolean;
  leading?: React.ReactNode;
  onClick: () => void;
}> = ({ label, sub, selected, leading, onClick }) => (
  <div
    role="option"
    aria-selected={!!selected}
    tabIndex={0}
    className="flex items-center cursor-pointer"
    style={{
      minHeight: '48px',
      padding: '6px 20px',
      gap: '12px',
      backgroundColor: selected ? CHIP_TEAL_BG : 'transparent',
    }}
    onClick={onClick}
    onKeyDown={e => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onClick();
      }
    }}
  >
    {leading}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        className="font-inter"
        style={{
          fontSize: '15px',
          fontWeight: 500,
          color: TEXT,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </div>
      {sub && (
        <div className="font-inter" style={{ fontSize: '12.5px', color: TEXT_DIM }}>
          {sub}
        </div>
      )}
    </div>
    {selected && <Icon name="check" color={TEAL} size={20} />}
  </div>
);

/* ------------------------------------------------------------------ */
/* Base attribute sheet                                                */
/* ------------------------------------------------------------------ */

export interface BaseAttributeSheetOption {
  id: string;
  attributeName: string;
}

export const BaseAttributeSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  language: any;
  options: BaseAttributeSheetOption[];
  selected: BaseAttributeSheetOption | null;
  loading: boolean;
  hasMore: boolean;
  onSearch: (keyword: string) => void;
  onLoadMore: () => void;
  onSelect: (option: BaseAttributeSheetOption | null) => void;
}> = ({ open, onClose, language, options, selected, loading, hasMore, onSearch, onLoadMore, onSelect }) => {
  const [keyword, setKeyword] = useState('');
  const lastSearchedRef = useRef('');

  useDebounceEffect(
    () => {
      if (!open) return;
      const next = keyword.trim();
      if (next === lastSearchedRef.current) return;
      lastSearchedRef.current = next;
      onSearch(next);
    },
    [keyword, open],
    300
  );

  useEffect(() => {
    if (!open) {
      if (lastSearchedRef.current !== '') {
        lastSearchedRef.current = '';
        onSearch('');
      }
      setKeyword('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { setLoaderRef } = useInfiniteScroll({
    hasMore,
    loading,
    onLoadMore: open ? onLoadMore : undefined,
  });

  const noneLabel = t(language, 'None', 'None');

  return (
    <BottomSheet open={open} onClose={onClose} title={t(language, 'Base Attribute', 'Base Attribute')}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          padding: '0 20px 10px',
        }}
      >
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '14px', top: '14px' }}>
            <Icon name="search" color={TEXT_DIM} size={20} />
          </span>
          <MobileTextInput
            type="search"
            inputMode="search"
            autoComplete="off"
            placeholder={t(language, 'Search base attributes', 'Search base attributes')}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            style={{ paddingLeft: '44px' }}
            aria-label={t(language, 'Search base attributes', 'Search base attributes')}
          />
        </div>
      </div>
      <div role="listbox">
        <ChoiceRow
          label={noneLabel}
          sub={t(language, 'No base attribute', 'No base attribute')}
          selected={!selected}
          onClick={() => onSelect(null)}
        />
        {selected && (
          <ChoiceRow
            label={selected.attributeName}
            selected
            onClick={() => onSelect(selected)}
          />
        )}
        {options.map(option => (
          <ChoiceRow
            key={option.id}
            label={option.attributeName}
            selected={false}
            onClick={() => onSelect(option)}
          />
        ))}
        {!loading && options.length === 0 && keyword.trim() !== '' && (
          <p
            className="font-inter"
            style={{ padding: '16px 20px', fontSize: '14px', color: TEXT_DIM }}
          >
            {t(language, 'No Results Found', 'No Results Found')}
          </p>
        )}
        <div ref={setLoaderRef} style={{ height: '1px' }} />
        {loading && (
          <div className="flex items-center justify-center" style={{ padding: '12px' }}>
            <span className="loader" />
          </div>
        )}
      </div>
    </BottomSheet>
  );
};

/* ------------------------------------------------------------------ */
/* Colours                                                             */
/* ------------------------------------------------------------------ */

/** Brand presets offered in the colour sheet. */
export const COLOR_PRESETS = [
  '#0F7679',
  '#0D5759',
  '#3AC0C4',
  '#EBF0F0',
  '#E7F1F1',
  '#DBE4E4',
  '#222525',
  '#05292A',
  '#FFFFFF',
  '#BA0E13',
];

const Swatch: React.FC<{ color: string; size?: number }> = ({ color, size = 24 }) => (
  <span
    aria-hidden
    style={{
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '8px',
      backgroundColor: color,
      border: `1px solid ${BORDER_STRONG}`,
      flex: 'none',
      display: 'inline-block',
    }}
  />
);

/** "Background  ▢ #0F7679  ›" — a 48px row that opens the colour sheet. */
export const ColorRow: React.FC<{
  label: string;
  value: string;
  onClick: () => void;
  /** Corner radius when the row sits inside a joined group. */
  radius?: string;
}> = ({ label, value, onClick, radius }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center cursor-pointer font-inter"
    style={{
      ...mobileInputStyle,
      gap: '12px',
      textAlign: 'left',
      ...(radius ? { borderRadius: radius } : null),
    }}
  >
    <span style={{ flex: 1, fontSize: '16px', color: TEXT }}>{label}</span>
    <Swatch color={value} />
    <span style={{ fontSize: '14px', color: TEXT_MUTED, fontFamily: MONO }}>{value}</span>
    <Icon name="chevd" color={TEXT_DIM} size={20} />
  </button>
);

export const ColorSheet: React.FC<{
  open: boolean;
  title: string;
  language: any;
  value: string;
  onChange: (hex: string) => void;
  onClose: () => void;
}> = ({ open, title, language, value, onChange, onClose }) => {
  const [hex, setHex] = useState(value);
  useEffect(() => {
    if (open) setHex(value);
  }, [open, value]);

  const commitHex = () => {
    let v = hex.trim();
    if (!v) return;
    if (!v.startsWith('#')) v = '#' + v;
    if (/^#[0-9A-Fa-f]{3}$/.test(v)) {
      const h = v.slice(1);
      v = `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
    }
    if (/^#[0-9A-Fa-f]{6}$/.test(v)) onChange(v.toUpperCase());
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={title}>
      <p
        className="font-inter"
        style={{ padding: '0 20px 8px', fontSize: '13px', fontWeight: 500, color: TEXT_DIM }}
      >
        {t(language, 'Presets', 'Presets')}
      </p>
      <div
        className="flex"
        style={{ flexWrap: 'wrap', gap: '10px', padding: '0 20px 16px' }}
        role="listbox"
      >
        {COLOR_PRESETS.map(preset => {
          const on = preset.toUpperCase() === (value || '').toUpperCase();
          return (
            <button
              key={preset}
              type="button"
              role="option"
              aria-selected={on}
              aria-label={preset}
              onClick={() => onChange(preset)}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: preset,
                border: on ? `2px solid ${TEAL}` : `1px solid ${BORDER_STRONG}`,
                boxShadow: on ? '0 0 0 2px #FFFFFF inset' : undefined,
                padding: 0,
              }}
            >
              {on && (
                <Icon
                  name="check"
                  color={['#FFFFFF', '#EBF0F0', '#E7F1F1', '#DBE4E4'].includes(preset.toUpperCase()) ? TEAL : '#FFFFFF'}
                  size={20}
                />
              )}
            </button>
          );
        })}
      </div>
      <div style={{ padding: '0 20px 12px' }}>
        <MobileField label={t(language, 'Hex', 'Hex')}>
          <div className="flex items-center" style={{ gap: '10px' }}>
            <Swatch color={/^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : value} size={32} />
            <MobileTextInput
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={7}
              placeholder="#RRGGBB"
              value={hex}
              onChange={e => setHex(e.target.value)}
              onBlur={commitHex}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitHex();
                }
              }}
              aria-label={t(language, 'Hex', 'Hex')}
              style={{ fontFamily: MONO }}
            />
          </div>
        </MobileField>
      </div>
      <label
        className="flex items-center cursor-pointer"
        style={{ minHeight: '48px', padding: '6px 20px', gap: '12px', position: 'relative' }}
      >
        <Icon name="sliders" color={TEXT_MUTED} size={20} />
        <span className="font-inter" style={{ flex: 1, fontSize: '15px', fontWeight: 500, color: TEXT }}>
          {t(language, 'Custom…', 'Custom…')}
        </span>
        <input
          type="color"
          aria-label={t(language, 'Custom…', 'Custom…')}
          value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#000000'}
          onChange={e => onChange(e.target.value.toUpperCase())}
          style={{ position: 'absolute', opacity: 0, width: '1px', height: '1px', left: 0, top: 0 }}
        />
        <Icon name="chev" color={TEXT_DIM} size={18} />
      </label>
      <div style={{ padding: '8px 20px 0' }}>
        <button
          type="button"
          onClick={onClose}
          className="font-inter cursor-pointer"
          style={{
            width: '100%',
            height: '48px',
            borderRadius: '30px',
            backgroundColor: TEAL,
            color: '#FFFFFF',
            fontSize: '15px',
            fontWeight: 500,
            border: 'none',
          }}
        >
          {t(language, 'Done', 'Done')}
        </button>
      </div>
    </BottomSheet>
  );
};

/* ------------------------------------------------------------------ */
/* Logo                                                                */
/* ------------------------------------------------------------------ */

export const LogoRow: React.FC<{
  language: any;
  src?: string | null;
  fileName?: string;
  inputId: string;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPreview: () => void;
  onRemove: () => void;
}> = ({ language, src, fileName, inputId, onFileChange, onPreview, onRemove }) => {
  const textButton: React.CSSProperties = {
    minHeight: '44px',
    padding: '0 14px',
    borderRadius: '30px',
    border: `1px solid ${BORDER_STRONG}`,
    backgroundColor: '#FFFFFF',
    color: TEAL,
    fontSize: '14px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const thumb: React.CSSProperties = {
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    backgroundColor: '#EBF0F0',
    border: 'none',
    padding: 0,
    overflow: 'hidden',
    flex: 'none',
  };
  return (
    <div
      className="flex items-center"
      style={{
        gap: '12px',
        padding: '12px',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
      }}
    >
      {src ? (
        <button
          type="button"
          aria-label={t(language, 'Preview', 'Preview')}
          onClick={onPreview}
          className="flex items-center justify-center cursor-pointer"
          style={thumb}
        >
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </button>
      ) : (
        <div aria-hidden className="flex items-center justify-center" style={thumb}>
          <PhotoIcon />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          className="font-inter"
          style={{
            fontSize: '14px',
            color: src ? TEXT : TEXT_DIM,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {src
            ? fileName || t(language, 'Logo', 'Logo')
            : t(language, 'No logo yet', 'No logo yet')}
        </p>
        <div className="flex items-center" style={{ gap: '8px', marginTop: '6px' }}>
          <label
            htmlFor={inputId}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                document.getElementById(inputId)?.click();
              }
            }}
            className="font-inter cursor-pointer"
            style={textButton}
          >
            {src ? t(language, 'Change', 'Change') : t(language, 'Add logo', 'Add logo')}
          </label>
          {src && (
            <button
              type="button"
              onClick={onRemove}
              className="font-inter cursor-pointer"
              style={{ ...textButton, color: DESTRUCTIVE }}
            >
              {t(language, 'Remove', 'Remove')}
            </button>
          )}
        </div>
      </div>
      <input
        type="file"
        accept="image/*"
        onChange={onFileChange}
        style={{ display: 'none' }}
        id={inputId}
      />
    </div>
  );
};

/** Full-screen image preview: backdrop tap and a 44px close both dismiss. */
export const ImagePreviewOverlay: React.FC<{
  open: boolean;
  src?: string | null;
  onClose: () => void;
  closeLabel: string;
}> = ({ open, src, onClose, closeLabel }) => {
  if (!open || !src) return null;
  return (
    <div
      onClick={onClose}
      className="flex items-center justify-center"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.88)',
        zIndex: 210,
        padding: '64px 16px 32px',
      }}
    >
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="flex items-center justify-center cursor-pointer"
        style={{
          position: 'absolute',
          top: 'calc(8px + env(safe-area-inset-top))',
          right: '8px',
          width: '44px',
          height: '44px',
          borderRadius: '22px',
          border: 'none',
          backgroundColor: 'rgba(255,255,255,0.14)',
        }}
      >
        <Icon name="close" color="#FFFFFF" size={22} />
      </button>
      <img
        src={src}
        alt=""
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px' }}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Step 2 — product cards                                              */
/* ------------------------------------------------------------------ */

export interface ProductCardItem {
  id: string;
  productName: string;
  sku: string;
  isActive: boolean;
  isAttributes: boolean;
}

/** Full-width card; the WHOLE card toggles the mapping. */
export const ProductCard: React.FC<{
  product: ProductCardItem;
  language: any;
  onToggle: (product: ProductCardItem) => void;
}> = ({ product, language, onToggle }) => (
  <div
    role="checkbox"
    aria-checked={product.isAttributes}
    tabIndex={0}
    onClick={() => onToggle(product)}
    onKeyDown={e => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onToggle(product);
      }
    }}
    className="flex items-center cursor-pointer"
    style={{
      width: '100%',
      minHeight: '60px',
      gap: '12px',
      padding: '10px 14px',
      borderRadius: '12px',
      border: product.isAttributes ? `1.5px solid ${TEAL}` : `1px solid ${BORDER}`,
      backgroundColor: product.isAttributes ? CHIP_TEAL_BG : '#FFFFFF',
      boxSizing: 'border-box',
    }}
  >
    <SelectBox checked={product.isAttributes} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <p
        className="font-inter"
        style={{
          fontSize: '15px',
          fontWeight: 500,
          lineHeight: '20px',
          color: TEXT,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
        }}
      >
        {product.productName}
      </p>
      <p
        style={{
          fontSize: '12px',
          lineHeight: '16px',
          color: TEXT_DIM,
          fontFamily: MONO,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          marginTop: '2px',
        }}
      >
        {product.sku}
      </p>
    </div>
    <Pill
      label={product.isActive ? t(language, 'Active', 'Active') : t(language, 'Inactive', 'Inactive')}
      kind={product.isActive ? 'ok' : 'off'}
    />
  </div>
);

/* ------------------------------------------------------------------ */
/* Step 2 — toolbar                                                    */
/* ------------------------------------------------------------------ */

/**
 * Search scrolls away with the list; only the 44px All / Assigned row stays
 * pinned under the header so the cards keep most of the viewport.
 */
export const MappingsToolbar: React.FC<{
  language: any;
  search: string;
  onSearchChange: (value: string) => void;
  onSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  filter: 'All' | 'Checked';
  onFilterChange: (value: 'All' | 'Checked') => void;
  selectedCount: number;
  inputRef?: React.Ref<HTMLInputElement>;
}> = ({ language, search, onSearchChange, onSearchKeyDown, filter, onFilterChange, selectedCount, inputRef }) => (
  <>
    <div style={{ position: 'relative', padding: '12px 16px 0' }}>
      <span style={{ position: 'absolute', left: '30px', top: '26px' }}>
        <Icon name="search" color={TEXT_DIM} size={20} />
      </span>
      <input
        ref={inputRef}
        type="search"
        inputMode="search"
        autoComplete="off"
        value={search}
        onChange={e => onSearchChange(e.target.value)}
        onKeyDown={onSearchKeyDown}
        placeholder={t(language, 'Search products', 'Search products')}
        aria-label={t(language, 'Search products', 'Search products')}
        className={`font-inter ${INPUT_FOCUS}`}
        style={{ ...mobileInputStyle, paddingLeft: '44px' }}
      />
      {search !== '' && (
        <button
          type="button"
          aria-label={t(language, 'Clear', 'Clear')}
          onClick={() => onSearchChange('')}
          className="flex items-center justify-center cursor-pointer"
          style={{
            position: 'absolute',
            right: '18px',
            top: '14px',
            width: '44px',
            height: '44px',
            border: 'none',
            background: 'transparent',
          }}
        >
          <Icon name="close" color={TEXT_DIM} size={18} />
        </button>
      )}
    </div>
    <div
      data-testid="mappings-sticky-row"
      style={{
        position: 'sticky',
        top: `${STICKY_TOP}px`,
        zIndex: 10,
        backgroundColor: '#FFFFFF',
        padding: '6px 16px',
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <Segmented<'All' | 'Checked'>
        ariaLabel={t(language, 'Filter', 'Filter')}
        value={filter}
        onChange={onFilterChange}
        options={[
          { value: 'All', label: t(language, 'All', 'All') },
          {
            value: 'Checked',
            label: `${t(language, 'Assigned', 'Assigned')}${selectedCount ? ` · ${selectedCount}` : ''}`,
          },
        ]}
      />
    </div>
  </>
);

/* ------------------------------------------------------------------ */
/* Sticky footer                                                       */
/* ------------------------------------------------------------------ */

export const StickyFooter: React.FC<{
  secondaryLabel: string;
  onSecondary: () => void;
  primaryLabel: string;
  onPrimary?: () => void;
  // kb.primaryProps(handler): makes this the Enter target; its onClick replaces onPrimary.
  primaryProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  /** Visually disabled; the tap still fires so the caller can surface inline errors. */
  primaryDisabled?: boolean;
  note?: string;
}> = ({ secondaryLabel, onSecondary, primaryLabel, onPrimary, primaryProps, primaryDisabled, note }) => (
  <div
    style={{
      position: 'sticky',
      bottom: 0,
      zIndex: 20,
      marginTop: 'auto',
      backgroundColor: '#FFFFFF',
      borderTop: `1px solid ${BORDER}`,
      padding:
        '12px 16px calc(12px + max(0px, env(safe-area-inset-bottom, 0px) - var(--finex-tabbar-h, 0px)))',
    }}
  >
    {note && (
      <p
        className="font-inter"
        style={{ fontSize: '12.5px', color: TEXT_DIM, marginBottom: '8px', textAlign: 'center' }}
      >
        {note}
      </p>
    )}
    <div className="flex items-center" style={{ gap: '10px' }}>
      <button
        type="button"
        onClick={onSecondary}
        className="font-inter cursor-pointer"
        style={{
          flex: '0 0 auto',
          minWidth: '104px',
          height: '48px',
          padding: '0 20px',
          borderRadius: '30px',
          border: `1px solid ${BORDER_STRONG}`,
          backgroundColor: '#FFFFFF',
          color: TEXT,
          fontSize: '15px',
          fontWeight: 500,
        }}
      >
        {secondaryLabel}
      </button>
      <button
        type="button"
        aria-disabled={!!primaryDisabled}
        onClick={onPrimary}
        className="font-inter cursor-pointer"
        style={{
          flex: 1,
          height: '48px',
          borderRadius: '30px',
          border: 'none',
          backgroundColor: primaryDisabled ? '#A3C7C8' : TEAL,
          color: '#FFFFFF',
          fontSize: '15px',
          fontWeight: 600,
        }}
        onPointerDown={e => {
          if (!primaryDisabled) (e.currentTarget as HTMLButtonElement).style.backgroundColor = TEAL_DARK;
        }}
        onPointerUp={e => {
          if (!primaryDisabled) (e.currentTarget as HTMLButtonElement).style.backgroundColor = TEAL;
        }}
        onPointerLeave={e => {
          if (!primaryDisabled) (e.currentTarget as HTMLButtonElement).style.backgroundColor = TEAL;
        }}
        {...primaryProps}
      >
        {primaryLabel}
      </button>
    </div>
  </div>
);
